import React, { useState, useEffect } from "react";
import { Box, Typography, Grid } from "@mui/material";
import ByzMuiSaveButton from "src/components/core-components/mui/buttons/ByzMuiSaveButton";
import ByzMuiCloseButton from "src/components/core-components/mui/buttons/ByzMuiCloseButton";
import ByzMuiTextField from "src/components/core-components/mui/inputs/ByzMuiTextField";
import OpenComponentDialog from "src/components/shared-components/modal/openComponentModal";
import { useTranslate } from "src/locales";
import { requestService } from "src/api/request/requestService";
import { useSnackbar } from "notistack";

interface EditNotesModalProps {
  open: boolean;
  onClose: () => void;
  prdId: number | string;
  actionName: string;
  initialNote?: string;
  initialCrNote?: string;
  onSuccess?: (note: string, crNote: string) => void;
}

export default function EditNotesModal({
  open,
  onClose,
  prdId,
  actionName,
  initialNote = "",
  initialCrNote = "",
  onSuccess,
}: EditNotesModalProps) {
  const { t } = useTranslate();
  const { enqueueSnackbar } = useSnackbar();
  const [note, setNote] = useState(initialNote);
  const [crNote, setCrNote] = useState(initialCrNote);
  const [loading, setLoading] = useState(false);

  // Reset form when modal opens with new data
  useEffect(() => {
    if (open) {
      setNote(initialNote);
      setCrNote(initialCrNote);
    }
  }, [open, initialNote, initialCrNote]);

  const handleSave = async () => {
    setLoading(true);
    try {
      const response = await requestService.updateRequestDetailNotes({
        prdId: Number(prdId),
        note: note,
        crNote: crNote,
      });
      
      if (response?.issuccess) {
        enqueueSnackbar(
          t("request-list.notes-updated-success", "default", {
            defaultValue: "Notlar başarıyla güncellendi",
          }),
          { variant: "success" }
        );
        onSuccess?.(note, crNote);
        onClose();
      } else {
        enqueueSnackbar(
          response?.message || t("errors.unknown-error", "default", { defaultValue: "Bir hata oluştu" }),
          { variant: "error" }
        );
      }
    } catch (error) {
      console.error("Error updating notes:", error);
      enqueueSnackbar(
        t("errors.unknown-error", "default", { defaultValue: "Bir hata oluştu" }),
        { variant: "error" }
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <OpenComponentDialog
      open={open}
      onClose={onClose}
      maxWidth="md"
      fullWidth
      hideHeader
      showPatientData={false}
    >
      <Box
        sx={{
          mx: -2,
          mt: 4,
          mb: 2,
          py: 1.5,
          px: 2,
          backgroundColor: "#F4F7FE",
          borderBottom: "1px solid #E0E0E0",
          textAlign: "left",
        }}
      >
        <Typography variant="h6" fontWeight={700} color="#111827">
          {actionName}
        </Typography>
      </Box>

      <Box
        sx={{
          px: { xs: 1, sm: 1.5 },
          pb: { xs: 2, sm: 3 },
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
            mb: 1.5,
          }}
        >
          <ByzMuiSaveButton onClick={handleSave} loading={loading} />
          <ByzMuiCloseButton onClick={onClose} />
        </Box>

        <Box sx={{ width: "100%", borderBottom: "1px solid black", mb: 3 }} />

        <Box sx={{ maxWidth: 800, mx: "auto", width: "100%" }}>
          <Grid container spacing={2}>
            {/* Note Field */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  pb: 1,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("request-list.columns.note", "default", { defaultValue: "Not" })}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ByzMuiTextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={note}
                onChange={(e) => setNote(e.target.value)}
              />
            </Grid>

            {/* Spacer row */}
            <Grid item xs={12} sx={{ height: 24 }} />

            {/* Consultation Note Field */}
            <Grid item xs={12} sm={6}>
              <Box
                sx={{
                  borderBottom: "1px solid #E0E0E0",
                  height: "100%",
                  display: "flex",
                  alignItems: "center",
                  pb: 1,
                }}
              >
                <Typography variant="subtitle2" fontWeight={600}>
                  {t("request-list.columns.consultation-note", "default", { defaultValue: "Konsültasyon Notu" })}
                </Typography>
              </Box>
            </Grid>
            <Grid item xs={12} sm={6}>
              <ByzMuiTextField
                fullWidth
                size="small"
                multiline
                rows={3}
                value={crNote}
                onChange={(e) => setCrNote(e.target.value)}
              />
            </Grid>
          </Grid>
        </Box>
      </Box>
    </OpenComponentDialog>
  );
}
